import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, ButtonBase } from "@mui/material";
import effiaLogo from "../../assets/logo/logo-effia.svg";

interface LanguageSelectionPopupProps {
    onSelect: () => void;
}

const languagesData = [
    {
        code: "fr",
        flag: "https://flagcdn.com/fr.svg",
        alt: "Français",
        name: "Français",
        description: "Continuer en français"
    },
    {
        code: "nl",
        flag: "https://flagcdn.com/nl.svg",
        alt: "Nederlands",
        name: "Nederlands",
        description: "Verdergaan in het Nederlands"
    },
    {
        code: "en",
        flag: "https://flagcdn.com/gb.svg",
        alt: "English",
        name: "English",
        description: "Continue in English"
    },
];

const LanguageSelectionPopup: React.FC<LanguageSelectionPopupProps> = ({
    onSelect,
}) => {
    const { i18n } = useTranslation();

    const [detectedLang, setDetectedLang] = React.useState("en");

    React.useLayoutEffect(() => {
        const browserLang = navigator.language.toLowerCase().split("-")[0];
        let langToSet = "en";

        if (browserLang === "nl" || browserLang === "de") {
            langToSet = "nl";
        } else if (browserLang === "fr") {
            langToSet = "fr";
        }

        setDetectedLang(langToSet);

        // Ensure navbar reflects the detected language even before selection
        i18n.changeLanguage(langToSet);
    }, [i18n]);

    const handleSelect = (code: string) => {
        i18n.changeLanguage(code);
        localStorage.setItem("languageSelected", "true");
        localStorage.setItem("languageSelectionOpened", "true");
        localStorage.setItem("selectedLanguage", code);
        localStorage.setItem("i18nextLng", code);
        onSelect();
    };

    const reorderedLanguages = React.useMemo(() => {
        let order = ["en", "fr", "nl"];
        if (detectedLang === "nl") {
            order = ["nl", "en", "fr"];
        } else if (detectedLang === "fr") {
            order = ["fr", "en", "nl"];
        }
        return order.map(code => languagesData.find(l => l.code === code)).filter(Boolean) as typeof languagesData;
    }, [detectedLang]);

    const popupTranslations = {
        en: {
            title: "Please choose your preferred language to continue.",
            footerText: "You can change your language at any time from the",
            settings: "menu settings"
        },
        fr: {
            title: "Veuillez choisir votre langue préférée pour continuer.",
            footerText: "Vous pouvez changer de langue à tout moment depuis les",
            settings: "paramètres du menu"
        },
        nl: {
            title: "Gelieve uw voorkeurstaal te kiezen om verder te gaan.",
            footerText: "U kunt uw taal op elk moment wijzigen via de",
            settings: "menu-instellingen"
        }
    };

    const currentText = popupTranslations[detectedLang as keyof typeof popupTranslations] || popupTranslations.en;

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 99999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0, 0, 0, 0.45)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
            }}
        >
            <Box
                sx={{
                    width: "440px",
                    borderRadius: "24px",
                    background: "#00000000",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.2)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        background: "#1b374c",
                        padding: "32px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                    }}
                >
                    <Box
                        component="img"
                        src={effiaLogo}
                        alt="EFFIA Logo"
                        sx={{ height: "48px" }}
                    />
                </Box>

                <Box sx={{ padding: "2vh 2vw", background: "#fff" }}>
                    <Typography
                        sx={{
                            fontFamily: '"Fira Sans", sans-serif',
                            fontSize: "14px",
                            color: "#666",
                            textAlign: "center",
                            marginBottom: "24px",
                        }}
                    >
                        {currentText.title}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {reorderedLanguages.map((lang) => (
                            <ButtonBase
                                key={lang.code}
                                onClick={() => handleSelect(lang.code)}
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    padding: "12px 20px",
                                    borderRadius: "12px",
                                    border: "1px solid #e0e0e0",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                    "&:hover": {
                                        borderColor: "#1b374c",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={lang.flag}
                                    alt={lang.alt}
                                    sx={{
                                        width: "40px",
                                        height: "auto",
                                        borderRadius: "4px",
                                        marginRight: "16px",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    }}
                                />
                                <Box sx={{ textAlign: "left" }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: "16px",
                                            color: "#1b374c",
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {lang.name}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "11px",
                                            color: "#888",
                                            marginTop: "2px",
                                        }}
                                    >
                                        {lang.description}
                                    </Typography>
                                </Box>
                            </ButtonBase>
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        padding: "16px 32px 32px",
                        textAlign: "center",
                        background: "#fff",
                        borderBottomLeftRadius: "24px",
                        borderBottomRightRadius: "24px",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 500,
                            color: "#444",
                        }}
                    >
                        {currentText.footerText}{" "}
                        <Box
                            component="span"
                            sx={{ color: "#f39325" }}
                        >
                            {currentText.settings}
                        </Box>
                        .
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default LanguageSelectionPopup;
